<template>
  <ejs-grid
    id="grid-search"
    ref="grid"
    :dataSource="allSearchResults"
    :allowPaging="true"
    :pageSettings="pageSettings"
    :locale="getLocale"
    :enableRtl="isRtl"
    :allowSelection="false"
    :queryCellInfo="cellProcessor"
    :rowDataBound="rowProcessor"
    :allowSorting="true"
    :sortSettings="sortOptions"
  >
    <e-columns>
      <e-column
        field="Name"
        :headerText="strings.tableColumnName"
        :template="searchResultLinkTemplate()"
        textAlign="Left"
        :customAttributes="{ 'data-tooltip': ''}"
        clipMode="Ellipsis"
        width=30%
      ></e-column>
      <e-column
        field="Author"
        :headerText="strings.tableColumnAuthor"
        textAlign="Left"
        clipMode="Ellipsis"
        width=20%
        hideAtMedia='(min-width: 800px)'
      ></e-column>
      <e-column
        field="Type"
        :headerText="strings.tableColumnType"
        :formatter="getFormattedType"
        textAlign="Left"
        clipMode="Ellipsis"
        width=10%
      ></e-column>
      <e-column
        field="Description"
        :visible="false"
        :headerText="strings.tableColumnDescription"
        textAlign="Left"
        clipMode="Ellipsis"
      ></e-column>
      <e-column
        field="CreatedDate"
        :headerText="strings.tableColumnCreated"
        :template="searchResultTimestampTemplate('CreatedDate')"
        textAlign="right"
        :customAttributes="{ 'data-tooltip': '' }"
        type="date"
        clipMode="Ellipsis"
        width=15%
      ></e-column>
      <e-column
        field="LastViewed"
        :visible="false"
        :headerText="strings.tableColumnLastViewed"
        :template="searchResultTimestampTemplate('LastViewed')"
        textAlign="right"
        :customAttributes="{ 'data-tooltip': '' }"
        type="date"
        clipMode="Ellipsis"
      ></e-column>
      <e-column
        field="ModifiedDate"
        :headerText="strings.tableColumnModifiedDate"
        :template="searchResultTimestampTemplate('ModifiedDate')"
        textAlign="right"
        :customAttributes="{ 'data-tooltip': '' }"
        type="date"
        clipMode="Ellipsis"
        width=15%
      ></e-column>
      <e-column
        field="Location"
        :headerText="strings.tableColumnLocation"
        :customAttributes="{ 'class': 'reverse-ellipsis' }"
        textAlign="Left"
        clipMode="Ellipsis"
        width=30%
        hideAtMedia='(min-width: 1000px)'
      ></e-column>
    </e-columns>
  </ejs-grid>
</template>

<script>
import { CONST } from '@/utils/constants'
import { Formatters } from '@/utils/formatters'
import { opensWithNewWorkspace } from '@/utils'

import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'

import ResultLink from '@/components/search/ResultLink'
import ResultTimestamp from '@/components/search/ResultTimestamp'
import { GridPlugin, Page, Sort } from '@syncfusion/ej2-vue-grids'

Vue.use(GridPlugin)

export default {
  data () {
    return {
      strings: {
        tableColumnType: this.translate('table search column type'),
        tableColumnName: this.translate('table search column name'),
        tableColumnDescription: this.translate('table search column description'),
        tableColumnCreated: this.translate('table search column created'),
        tableColumnAuthor: this.translate('table search column author'),
        tableColumnLastViewed: this.translate('table search column last viewed'),
        tableColumnModifiedDate: this.translate('table search column modified date'),
        tableColumnLocation: this.translate('table search column location'),
      },
      sortOptions: { columns: [{ field: 'Name', direction: 'Ascending' }], allowUnsort: false },
      pageSettings: { pageSize: CONST.SearchSettings.paging, pageSizes: [ 20, 50, 100, 250 ] },
      searchResultLinkTemplate: () => () => ({
        template: {
          extends: ResultLink,
          propsData: {
            canCreateWorkspaces: this.canCreateWorkspaces
          }
        }
      }),
      searchResultTimestampTemplate: (field) => () => ({
        template: {
          extends: ResultTimestamp,
          propsData: {
            field
          }
        }
      })
    }
  },
  provide: {
    grid: [Page, Sort]
  },
  computed: {
    ...mapGetters({
      allSearchResults: 'search/allSearchResults',
      isRtl: 'i18n/isRtl',
      getLocale: 'i18n/getLocale',
      canCreateWorkspaces: 'user/canNameWorkspace'
    })
  },
  methods: {
    cellProcessor (item) {
      // Add Cell Tooltips
      this.setTooltip(item)
    },
    rowProcessor (item) {
      // Disable Rows that Require Creating a Workspace
      this.disableDisallowed(item)
    },
    setTooltip (item) {
      // Get Data Tooltip Attr value
      const override = item.cell.getAttribute('data-tooltip')
      // Check if purposely set to Empty ('')
      const isEmpty = (typeof override === 'string' && override.trim() === '')
      // Add Tooltip if not Blank
      if (!isEmpty) {
        // Use Override value or InnerText
        item.cell.setAttribute('title', override || item.cell.innerText)
      }
    },
    disableDisallowed (item) {
      // Make Flags
      const restricted = opensWithNewWorkspace(item.data.Type)
      const capable = this.canCreateWorkspaces
      const isAllowed = (capable || !restricted)
      // Disable Rows for Viewer Users
      if (!isAllowed) {
        item.row.classList.add('disabled')
      }
    },
    getFormattedType (column, data) {
      const type = data[column.field]
      return Formatters.Type(type)
    },
    translate (string, object) {
      return this.$i18n.translate(string, object)
    }
  }
}
</script>

<style lang="scss">
@import "@/styles/components/search/_search-table.scss";
</style>
